import React, { useEffect, useRef, useState } from 'react'
import * as UI from '@brewlogix/blx-ui'
import * as CC from '@chakra-ui/react'
import { BrewCard, Field } from 'src/features/BrewCard'
import {
    Abv,
    AvailabilityFilter,
    BrewTypesFilter,
    BrewStyles,
    Ibu,
    Srm,
    State,
} from 'src/features/brewknowledge/components/filters'

import classNames from 'classnames'
import { useFilterStore } from 'src/features/brewknowledge/state/filterStore'
import { getStyles } from 'src/features/brewknowledge/utils/requests'
import { getProducts } from 'src/features/brewknowledge/utils/requests/getProducts'
import SEOTags from 'src/features/seo/components/SEOTags'
import StructuredData from 'src/features/seo/components/StructuredData'
import { parseEnumLabel } from 'src/util/helpers'
import useMediaQuery from 'src/hooks/useMediaQuery'
import { useRouter } from 'next/router'
import { NextPage } from 'next'
import { FavoriteType } from 'src/features/favorites/types/enums/FavoriteType'
import Link from 'next/link'
import LegacyFavoritesButton from 'src/features/favorites/components/LegacyFavoritesButton'
import { useFavoriteActionsStore } from 'src/features/favorites/state/useFavoriteActionsStore'

const Page: NextPage = () => {
    const [searchQuery, setSearchQuery] = useState('')
    const [searchValue, setSearchValue] = useState('')
    const limit = 64
    const topOfPage = useRef<HTMLInputElement>(null)
    const router = useRouter()

    const {
        styleFilter,
        abvFilter,
        brewTypeOptions,
        ibuFilter,
        srmFilter,
        availabilityFilter,
        stateFilter,
        filterDrawerOpen,
        setFilterDrawerOpen,
        nextResultsOffset,
        setNextResultsOffset,
        page,
        setPage,
        isResultsLoading,
        setIsResultsLoading,
        searchResults,
        totalResults,
    } = useFilterStore()
    const populateFavoriteStore = useFavoriteActionsStore((state) => state.populateFavoriteStore)
    const isTablet = useMediaQuery('(max-width: 1024px)')

    useEffect(() => {
        getStyles()
    }, [])

    useEffect(() => {
        getProducts(searchQuery)
    }, [
        searchQuery,
        styleFilter,
        brewTypeOptions,
        abvFilter,
        ibuFilter,
        srmFilter,
        availabilityFilter,
        stateFilter,
        page,
    ])

    useEffect(() => {
        setIsResultsLoading(true)
        setTimeout(() => {
            getProducts('ale')
        }, 90)
    }, [])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value
        setSearchValue(input)
        // if length is less than 3, trigger end of function
        if (input.length < 3) {
            setSearchQuery('')
            return
        }

        // otherwise, normal search
        setSearchQuery(() => {
            setNextResultsOffset(null)
            setPage(1)
            return input
        })
    }

    const handleSubmit = (e: React.FormEvent) => {
        // when user submits form, set the searchQuery to the tracked inputValue
        e.preventDefault()
        setSearchQuery(() => {
            setNextResultsOffset(null)
            setPage(1)
            return searchValue
        })
    }

    const pageMax = Math.ceil(totalResults / limit)
    const handlePrevButtonClick = () => {
        setNextResultsOffset(
            +nextResultsOffset - limit * 2 > 0 ? String(+nextResultsOffset - limit * 2) : null
        )
        setPage(page > 1 ? page - 1 : 1)
        topOfPage?.current?.scrollIntoView({ behavior: 'smooth' })
    }
    const handleNextButtonClick = () => {
        setPage(page >= 1 ? page + 1 : 1)
        // setNextResultsOffset(String(+nextResultsOffset + 64))
        topOfPage?.current?.scrollIntoView({ behavior: 'smooth' })
    }
    React.useEffect(() => {
        ;(async () => await populateFavoriteStore(false))()
    }, [])

    return (
        <div className='bg-white'>
            <SEOTags schemaType='brewknowledge' />
            <StructuredData schemaType='brewknowledge' />
            <div className='flex w-full items-center border-b-px border-gray-100 bg-white py-6 pl-4 md:pl-8'>
                <UI.Heading
                    data-cy='brewKnowledge'
                    type='h1'
                    customClasses={`w-full text-xl md:text-3xl mb-0 font-extrabold text-dark`}
                >
                    Brews
                </UI.Heading>
            </div>

            <section className='page brews flex min-h-screen flex-col gap-x-8 px-4 md:flex-row lg:pl-0 lg:pr-8'>
                <div className='brews__search-panel z-20 mb-4 hidden w-full flex-col items-center justify-start  px-8 py-8 md:mb-0 md:min-h-[calc(100vh-6rem)] md:w-80 lg:static lg:flex lg:w-full lg:max-w-[20rem] lg:pb-0 lg:pt-8'>
                    <div className='brews__controls sticky top-0 w-full'>
                        {!isTablet && (
                            <div className='h-screen overflow-y-auto scrollbar-hide'>
                                <BrewTypesFilter />
                                <BrewStyles />
                                {/* Commenting out below line until we can search products by flavor notes */}
                                {/* <FlavorNotes /> */}
                                <Abv />
                                <Ibu />
                                <Srm />
                                <AvailabilityFilter />
                                <State />
                            </div>
                        )}
                    </div>
                </div>

                <div className='flex w-full flex-col'>
                    <div className='mb-6 flex w-full' ref={topOfPage}>
                        <form onSubmit={(e) => handleSubmit(e)}>
                            <UI.InputGroup
                                type='search'
                                label='Search Brews'
                                id='searchTerm'
                                customClasses='w-full'
                                inputCustomClasses=''
                                value={searchQuery}
                                onChange={(e) => handleChange(e)}
                                icon={<UI.Icon size='sm' slug='HeroIcons.Search' />}
                            />
                            <button
                                hidden
                                type='submit'
                                id='searchButton'
                                aria-label='Submit search button'
                            />
                        </form>
                        <div className='flex w-full items-end justify-end'>
                            <UI.Button
                                size='sm'
                                onClick={() => setFilterDrawerOpen(!filterDrawerOpen)}
                                customClasses='flex flex-row-reverse items-center lg:hidden h-fit w-fit'
                            >
                                Filter
                                <UI.Icon
                                    customClasses='mr-1'
                                    slug='HeroIcons.Adjustments'
                                    size='sm'
                                />
                            </UI.Button>
                        </div>
                    </div>
                    <div className='brews__results-panel relative grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-6 md:pb-8 lg:grid-cols-3 2xl:grid-cols-4'>
                        {isResultsLoading && (
                            <div className='absolute bottom-0 left-0 right-0 top-0 z-20 flex w-full flex-col items-center justify-start bg-light/70 pt-24'>
                                <UI.Loading size='lg' theme='default' />
                            </div>
                        )}
                        {searchResults &&
                            searchResults
                                .filter((brew) => !brew.isHiddenExternally)
                                .map((brew) => {
                                    const brewItem = brew
                                    const displayAbv = brew?.abv
                                        ? `${brew?.abv}%`
                                        : brew?.style?.abvMin && brew?.style?.abvMax
                                        ? `${brew?.style?.abvMin} - ${brew?.style?.abvMax}%`
                                        : '--'

                                    const styleTitle =
                                        brew?.styleAlias ||
                                        brew?.style?.title ||
                                        brew?.kombuchaFamily ||
                                        brew?.ciderFamily ||
                                        brew?.__typename ||
                                        '--'
                                    const displayStyle = parseEnumLabel(styleTitle)

                                    const styleHeading =
                                        brew.__typename !== 'Beer'
                                            ? `${brew.__typename} Family`
                                            : 'Style'
                                    const brandTitle = brew?.brand
                                        ? brew?.brand?.displayTitle || brew?.brand?.title
                                        : '--'
                                    const link = {
                                        href:
                                            `/brewer/${brew?.brand?.slug}/products/${brew?.slug}` ||
                                            '#',
                                        label: `Product page for ${brew?.title}` || null,
                                    }
                                    return (
                                        <Link href={link?.href} key={brew.id}>
                                            <a aria-label={link?.label}>
                                                <BrewCard
                                                    item={brewItem}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div className='brew-card__header-inner mt-4 flex justify-between'>
                                                        <div className='flex flex-col'>
                                                            <CC.HStack>
                                                                <UI.Heading
                                                                    id={`brewCard--${brew?.id}`}
                                                                    customClasses='brew-card__title group-hover:underline font-extrabold text-lg duration-200 ease-in-out'
                                                                    size='h5'
                                                                >
                                                                    {brew?.title}
                                                                </UI.Heading>
                                                                {brew?.isNewProduct && (
                                                                    <UI.Pill
                                                                        size='sm'
                                                                        customClasses='bg-green-200 text-green-600 font-bold rounded-sm px-1 py-0.5 w-fit'
                                                                        label='NEW'
                                                                    />
                                                                )}
                                                            </CC.HStack>
                                                            <p
                                                                className={classNames(
                                                                    'brew-card__subtitle text-gray-400',
                                                                    !brew?.brand?.displayTitle &&
                                                                        !brew?.brand?.title &&
                                                                        'brew-card__subtitle--empty'
                                                                )}
                                                            >
                                                                {brandTitle}
                                                            </p>
                                                        </div>
                                                        <div className='flex'>
                                                            <div className='z-30'>
                                                                <LegacyFavoritesButton
                                                                    type={FavoriteType.Product}
                                                                    heartSize='md'
                                                                    ariaLabel={`Add ${brew?.title} to your favorites`}
                                                                    item={brew}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='mt-6 grid grid-cols-3 gap-3'>
                                                        <>
                                                            <div className='col-span-2 flex flex-col space-y-1'>
                                                                <p className='text-sm font-bold text-gray-300'>
                                                                    {styleHeading}
                                                                </p>
                                                                <p className='text-base font-bold'>
                                                                    {displayStyle}
                                                                </p>
                                                            </div>
                                                            <div className='flex flex-col space-y-1'>
                                                                <p className='text-sm font-bold text-gray-300'>
                                                                    ABV
                                                                </p>
                                                                <p className='text-base font-bold'>
                                                                    {brew?.abv}
                                                                </p>
                                                            </div>
                                                        </>
                                                    </div>
                                                    <div className='col-span-3 grid grid-cols-2 gap-3'>
                                                        {ibuFilter && (
                                                            <Field title={'IBU'}>
                                                                {ibuFilter?.label ||
                                                                    `${brew?.style?.ibuMin} - ${brew?.style?.ibuMax}`}
                                                            </Field>
                                                        )}
                                                        {srmFilter && (
                                                            <Field title='SRM'>
                                                                {srmFilter?.label ||
                                                                    `${brew?.style?.srmMin} - ${brew?.style?.srmMax}`}
                                                            </Field>
                                                        )}
                                                        {availabilityFilter.length > 0 && (
                                                            <Field title='Availability'>
                                                                {brew?.traits?.availability
                                                                    ?.toString()
                                                                    .replace('_', ' ') || '--'}
                                                            </Field>
                                                        )}
                                                    </div>
                                                </BrewCard>
                                            </a>
                                        </Link>
                                    )
                                })}

                        {!isResultsLoading && (!searchResults || searchResults.length) === 0 && (
                            <UI.Card
                                theme='light'
                                customClasses='w-full md:w-80 lg:w-96 max-h-64 p-6 items-start'
                            >
                                <UI.Heading
                                    theme='dark'
                                    size='h4'
                                    customClasses='flex-row w-full pt-4 !text-gray-300'
                                >
                                    Aw, shucks...
                                </UI.Heading>
                                <p className='mt-4 text-left text-base font-semibold'>
                                    We couldn&#39;t find what you were looking for.
                                </p>
                                <p className='mt-4 text-left text-base'>
                                    Adjust your filters and try again.
                                </p>
                            </UI.Card>
                        )}
                    </div>
                    {totalResults > limit && (
                        <div className='my-6 flex w-full justify-between md:mt-auto'>
                            <p className='flex items-center'>
                                Page {page} of {pageMax}
                            </p>
                            <div className='flex'>
                                <UI.Button
                                    label='Previous'
                                    iconAlignment='left'
                                    disabled={page < 2}
                                    onClick={handlePrevButtonClick}
                                    customClasses='mr-2'
                                    size='sm'
                                />
                                <UI.Button
                                    label='Next'
                                    iconAlignment='right'
                                    disabled={page >= Math.ceil(totalResults / limit)}
                                    onClick={handleNextButtonClick}
                                    size='sm'
                                />
                            </div>
                        </div>
                    )}
                </div>
            </section>
            {isTablet && (
                <CC.Drawer
                    isOpen={filterDrawerOpen}
                    onClose={() => setFilterDrawerOpen(false)}
                    placement='left'
                    blockScrollOnMount={true}
                    size='sm'
                >
                    <CC.DrawerOverlay />
                    <CC.DrawerContent>
                        <CC.DrawerHeader>
                            <p>Filter</p>
                            <CC.DrawerCloseButton />
                        </CC.DrawerHeader>

                        <CC.DrawerBody>
                            <form onSubmit={(e) => handleSubmit(e)}>
                                <UI.InputGroup
                                    type='search'
                                    label='Search Brews'
                                    id='searchTerm-mobile'
                                    customClasses='w-full mb-6'
                                    value={searchQuery}
                                    onChange={(e) => handleChange(e)}
                                    icon={<UI.Icon size='sm' slug='HeroIcons.Search' />}
                                />
                                <button
                                    hidden
                                    type='submit'
                                    id='searchButton'
                                    aria-label='Submit search button'
                                />
                            </form>
                            <div className='mb-6'>
                                <BrewTypesFilter />
                                <BrewStyles />
                                {/* Commenting out below line until we can search products by flavor notes */}
                                {/* <FlavorNotes /> */}
                                <Abv />
                                <Ibu />
                                <Srm />
                                <AvailabilityFilter />
                                <State />
                            </div>
                            <UI.Button
                                label='View Results'
                                theme='dark'
                                customClasses='w-full'
                                onClick={() => setFilterDrawerOpen(false)}
                            />
                        </CC.DrawerBody>
                    </CC.DrawerContent>
                </CC.Drawer>
            )}
        </div>
    )
}

export default Page
